import { IBusinessRepository } from '@/businessRepository/interfaces/businessRepository';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { reactive } from 'vue';
import { useRoute } from 'vue-router';
import { defineStore } from 'pinia';

/**
 * 詳細画面で表示されている取引情報を管理する
 * SelectableSimpleTableContentでの操作がメイン
 */
export const useSelectedBusinessStore = defineStore('SelectedBusiness', () => {
  const state = reactive<ISelectedBusinessState>({
    businesses: [],
    selectValue: {
      warehouse: '',
      supplierName: '',
      businessNumber: '',
      businessName: '',
      package: '',
    },
    options: {
      warehouse: [],
      supplierName: [],
      businessNumber: [],
      businessName: [],
      package: [],
    },
    selectedBusinessId: '',
    aggregateBusinessIdsHasRecommendOrder: [],
    recommendOrderPagingOption: {
      pageSize: 0,
      pageIndex: 0,
    },
  });

  const route = useRoute();

  /**
   * ステートを初期化する
   */
  const $reset = () => {
    state.businesses = [];
    state.selectValue = {
      warehouse: '',
      supplierName: '',
      businessNumber: '',
      businessName: '',
      package: '',
    };
    state.options = {
      warehouse: [],
      supplierName: [],
      businessNumber: [],
      businessName: [],
      package: [],
    };
    state.selectedBusinessId = '';
  };

  /**
   * ステートの初期化と取引情報の取得を行う
   * @param businessRepository - 取引情報の永続化の責務を負うクラス
   * @param propBusiness - プロパティを持つビジネス
   */
  const fetchState = async (businessRepository: IBusinessRepository, propBusiness: IBusiness) => {
    // TODO フィルターAPIを叩いてここらへんの中身を実装する必要あり
    // 今は一旦封殺
    state.businesses = [propBusiness];
    state.selectValue.warehouse = propBusiness.warehouse;
    state.selectValue.supplierName = propBusiness.supplierName;
    state.selectValue.businessNumber = propBusiness.businessNumber;
    state.selectValue.businessName = propBusiness.businessName ?? '';
    state.selectValue.package = propBusiness.package;
    state.selectedBusinessId = propBusiness.businessId;
    _updateOption('warehouse');
    _updateOption('supplierName');
    _updateOption('businessNumber');
    _updateOption('businessName');
    _updateOption('package');
    // const businessesResult = await GetAllBusinessUseCase.Execute(businessRepository);
    // switch (businessesResult.status.type) {
    //   case 'success':
    //     // 集計業のみに絞る
    //     state.businesses = businessesResult.status.data.filter((business) => business.aggregateGroup?.length);
    //     const currentBusiness = state.businesses.find((business) => business.businessId === route.params.id);
    //     if (currentBusiness) {
    //       state.selectValue.warehouse = currentBusiness.warehouse;
    //       state.selectValue.supplierName = currentBusiness.supplierName;
    //       state.selectValue.businessNumber = currentBusiness.businessNumber;
    //       state.selectValue.businessName = currentBusiness.businessName ?? '';
    //       state.selectValue.package = currentBusiness.package;
    //       state.selectedBusinessId = currentBusiness.businessId;
    //     }
    //     _updateOption('warehouse');
    //     _updateOption('supplierName');
    //     _updateOption('businessNumber');
    //     _updateOption('businessName');
    //     _updateOption('package');
    //     break;
    //   case 'error':
    //     console.error('fetchState error');
    //     openFailToAcquireModal();
    //     break;
    // }
  };

  /**
   * オプションの値を更新する
   * @param key - キー
   * @param value - 値
   */
  const updateSelectValue = (key: keyof typeof state.selectValue, value: string) => {
    state.selectValue[key] = value;
    _updateOption(key);
  };

  /**
   * 選択されたビジネスのIDを取得する
   * @param newValue - 選択された値
   */
  const updateSelectedBusinessId = (newValue: typeof state.selectValue) => {
    const currentBusiness = state.businesses.find((business) => {
      return (
        business.warehouse === newValue.warehouse &&
        business.supplierName === newValue.supplierName &&
        business.businessNumber === newValue.businessNumber &&
        business.businessName === newValue.businessName &&
        business.package === newValue.package
      );
    });
    state.selectedBusinessId = currentBusiness?.businessId ?? '';
  };

  /**
   * 選択された項目にマッチしている取引が存在しないかどうか
   */
  const isNotExitSelectedBusiness = () => {
    return state.selectedBusinessId === '';
  };

  //#region private関数

  /**
   * ビジネスのプロパティが一致するかどうか
   * 選択された値が空文字の場合は常にtrueを返す
   * @param target - ターゲット
   * @param selectedValue - 選択された値
   */
  const _equalsBusinessProperty = (target: string, selectedValue: string) => {
    return target === selectedValue || selectedValue === '';
  };

  /**
   * 指定されたキーに基づいてビジネスリストをフィルタリングし、同じ名前のオプションリストを更新する
   * @param key - 更新とフィルタリングに使用するキー
   */
  function _filterAndUpdateOption(key: keyof Options & keyof IBusiness) {
    state.options[key] = Array.from(
      new Set(
        state.businesses
          .filter((business) =>
            Object.keys(state.selectValue).every(
              (selectKey) =>
                selectKey === key || _equalsBusinessProperty(business[selectKey as keyof IBusiness] as string, state.selectValue[selectKey])
            )
          )
          .map((business) => business[key] as string)
      )
    ).filter(Boolean); // undefined または null を除外
  }

  /**
   * 選択されたキーに基づいて他のオプションの値を更新する
   * @param changedKey - 変更されたキー
   */
  const _updateOption = (changedKey: keyof Options) => {
    // 変更されたキー以外の全てのキーを更新する
    const allKeys: (keyof Options)[] = ['warehouse', 'supplierName', 'businessNumber', 'businessName', 'package'];
    allKeys.forEach((key) => {
      if (key !== changedKey) {
        _filterAndUpdateOption(key);
      }
    });
  };

  //#endregion

  return {
    state,
    fetchState,
    updateSelectValue,
    updateSelectedBusinessId,
    isNotExitSelectedBusiness,
    $reset,
  };
});

/**
 * 取引情報の状態
 */
interface ISelectedBusinessState {
  businesses: IBusiness[];
  selectValue: {
    warehouse: string;
    supplierName: string;
    businessNumber: string;
    businessName: string;
    package: string;
  };
  options: Options;
  selectedBusinessId: string;
  aggregateBusinessIdsHasRecommendOrder: string[];
  recommendOrderPagingOption: {
    pageSize: number;
    pageIndex: number;
  };
}

/**
 * Selectで選択できるオプション
 */
interface Options {
  warehouse: string[];
  supplierName: string[];
  businessNumber: string[];
  businessName: string[];
  package: string[];
}
